import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Star, Award, MessageCircle } from 'lucide-react';
import { services, offers, testimonials, salonInfo } from '../data/mock';

const Home = () => {
  const featuredOffer = offers.find(offer => offer.featured) || offers[0];

  return (
    <div className="pt-24 md:pt-28">
      {/* Hero Section - Full Screen */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage: 'url(/salon-exterior.png)',
          }}
        ></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center mb-8">
            <Sparkles className="w-8 h-8 text-amber-600 mr-3 animate-pulse" />
            <span className="text-amber-600 font-semibold tracking-[0.3em] uppercase text-sm md:text-base">
              Women-Only Luxury Salon
            </span>
          </div>
          <h1 className="text-6xl md:text-8xl lg:text-9xl font-bold text-white mb-8 leading-none">
            Where <span className="text-amber-600">Elegance</span><br />
            Meets Excellence
          </h1>
          <p className="text-2xl md:text-3xl text-gray-300 mb-12 leading-relaxed max-w-4xl mx-auto font-light">
            Discover your signature style in our exclusive sanctuary of beauty
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link
              to="/booking"
              className="group inline-flex items-center justify-center px-12 py-5 bg-amber-600 text-white rounded-none hover:bg-amber-700 transition-all duration-300 font-semibold text-lg uppercase tracking-wider"
            >
              Book Your Experience
              <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-2 transition-transform" />
            </Link>
            <Link
              to="/offers"
              className="inline-flex items-center justify-center px-12 py-5 bg-transparent text-white border-2 border-white rounded-none hover:bg-white hover:text-black transition-all duration-300 font-semibold text-lg uppercase tracking-wider"
            >
              View Special Offers
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Featured Offer Banner */}
      <section className="bg-amber-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between text-center md:text-left gap-4">
            <div className="flex items-center">
              <Award className="w-7 h-7 text-amber-600 mr-3" />
              <span className="font-bold text-gray-900 text-xl">
                {featuredOffer.title}
              </span>
            </div>
            <div className="flex items-center gap-6">
              <span className="text-gray-700 text-lg">{featuredOffer.discount}</span>
              <Link
                to="/offers"
                className="px-8 py-3 bg-amber-600 text-white rounded-none hover:bg-amber-700 transition-all duration-200 font-semibold uppercase tracking-wider"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview - Grid Layout */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="text-amber-600 font-semibold tracking-[0.3em] uppercase text-sm mb-4 block">
              Our Signature
            </span>
            <h2 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">
              Premium <span className="text-amber-600">Services</span>
            </h2>
            <p className="text-2xl text-gray-600 max-w-3xl mx-auto font-light">
              Expertly curated treatments designed to enhance your natural beauty
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.slice(0, 3).map((service, index) => (
              <div
                key={service.id}
                className="group relative overflow-hidden bg-black"
              >
                <div className="aspect-[3/4] relative overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-70"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  
                  {/* Number Badge */}
                  <div className="absolute top-8 left-8 w-16 h-16 border-2 border-amber-600 flex items-center justify-center">
                    <span className="text-amber-600 text-2xl font-bold">0{index + 1}</span>
                  </div>
                </div>
                
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <div className="text-sm font-medium text-amber-600 mb-3 uppercase tracking-wider">{service.category}</div>
                  <h3 className="text-3xl font-bold mb-4">{service.name}</h3>
                  <p className="text-gray-300 text-base mb-6">{service.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-amber-600">{service.price}</span>
                    <Link
                      to="/services"
                      className="inline-flex items-center text-white hover:text-amber-600 font-semibold uppercase tracking-wider transition-colors"
                    >
                      View Details
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link
              to="/services"
              className="inline-flex items-center px-12 py-5 bg-black text-white rounded-none hover:bg-amber-600 transition-all duration-300 font-semibold text-lg uppercase tracking-wider"
            >
              Explore All Services
              <ArrowRight className="ml-3 w-6 h-6" />
            </Link>
          </div>
        </div>
      </section>

      {/* Special of the Day */}
      <section className="py-32 bg-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-amber-600 font-semibold tracking-[0.3em] uppercase text-sm mb-4 block">
                Special of the Day
              </span>
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                {featuredOffer.title}
              </h2>
              <p className="text-2xl text-gray-700 mb-8 font-light leading-relaxed">
                {featuredOffer.description}
              </p>
              <div className="flex items-baseline gap-4 mb-10">
                <span className="text-6xl font-bold text-amber-600">{featuredOffer.discount}</span>
              </div>
              <Link
                to="/booking"
                className="inline-flex items-center px-12 py-5 bg-amber-600 text-white rounded-none hover:bg-amber-700 transition-all duration-300 font-semibold text-lg uppercase tracking-wider"
              >
                Book Now
                <ArrowRight className="ml-3 w-6 h-6" />
              </Link>
            </div>
            <div className="relative">
              <img
                src={featuredOffer.image}
                alt={featuredOffer.title}
                className="w-full h-[600px] object-cover shadow-2xl"
              />
              <div className="absolute -bottom-8 -left-8 bg-white p-8 shadow-xl max-w-xs">
                <p className="text-gray-600 text-sm mb-2">Valid Until</p>
                <p className="text-2xl font-bold text-gray-900">{featuredOffer.validUntil}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Numbered Grid */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="text-amber-600 font-semibold tracking-[0.3em] uppercase text-sm mb-4 block">
              The Difference
            </span>
            <h2 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6">
              Why Choose <span className="text-amber-600">Posh Signature</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {[
              { num: "01", title: "Women-Only Sanctuary", desc: "Complete privacy and comfort in our exclusive women-only environment" },
              { num: "02", title: "Expert Professionals", desc: "Certified stylists and therapists trained in latest luxury techniques" },
              { num: "03", title: "Premium Products", desc: "Finest international beauty brands for outstanding results" },
              { num: "04", title: "Luxury Experience", desc: "Elegant ambiance with personalized care and attention to detail" }
            ].map((item) => (
              <div key={item.num} className="flex gap-8">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 border-2 border-amber-600 flex items-center justify-center">
                    <span className="text-amber-600 text-3xl font-bold">{item.num}</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-xl text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-32 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="text-amber-600 font-semibold tracking-[0.3em] uppercase text-sm mb-4 block">
              Client Love
            </span>
            <h2 className="text-5xl md:text-7xl font-bold mb-6">
              What Our Clients <span className="text-amber-600">Say</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.slice(0, 4).map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white/5 backdrop-blur-sm p-10 border border-white/10 hover:border-amber-600 transition-all duration-300"
              >
                <div className="flex items-center mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 text-amber-600 fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 text-xl mb-8 italic leading-relaxed">"{testimonial.comment}"</p>
                <div>
                  <div className="font-bold text-white text-lg">{testimonial.name}</div>
                  <div className="text-amber-600 text-sm uppercase tracking-wider">{testimonial.service}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Full Width */}
      <section className="relative py-40 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/salon-spa-area.png)',
          }}
        >
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-8">
            Ready to Experience<br />
            <span className="text-amber-600">Luxury?</span>
          </h2>
          <p className="text-2xl text-gray-300 mb-12 max-w-3xl mx-auto">
            Book your appointment today and discover why we're the most trusted beauty salon for women
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to="/booking"
              className="inline-flex items-center justify-center px-12 py-5 bg-amber-600 text-white rounded-none hover:bg-amber-700 transition-all duration-300 font-bold text-lg uppercase tracking-wider shadow-2xl"
            >
              Book Appointment
              <ArrowRight className="ml-3 w-6 h-6" />
            </Link>
            <a
              href={`https://wa.me/${salonInfo.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-12 py-5 bg-green-600 text-white rounded-none hover:bg-green-700 transition-all duration-300 font-bold text-lg uppercase tracking-wider"
            >
              <MessageCircle className="mr-3 w-6 h-6" />
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
